"use client";

import type { AlbumMusicPlayerBlock, AudioSample } from "@mw/shared/payload-types";

import { useNativeAudio } from "@/app/_providers/native-audio/NativeAudioProvider";
import { getAudioUrl, isEntity, formatLink, getVideoUrl } from "@utilities/payload";
import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { TabPanel, TabPanels, Tabs, type Key } from "react-aria-components";

import { Block } from "../Block";
import { AlbumArtPlayer } from "./AlbumArtPlayer";
import { FloatingMusicWidget } from "./FloatingMusicWidget";
import { Link } from "@ui/components/core/Link";
import { TabList } from "@ui/components/core/TabList";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import { ModalVideoPlayer } from "@ui/components/core/ModalVideoPlayer";
import LazyVideoPlayer from "@ui/components/core/LazyVideoPlayer";
import Play16 from "@ui/icons/16x16/play-16.svg";
import "./album-music-player.css";

const VideoCtaButton = forwardRef<
  HTMLButtonElement,
  { videoUrl: string; label: string } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ videoUrl, label, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type='button'
      className='tw-group tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-full tw-bg-white tw-shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_0_rgba(0,0,0,0.04),0_2px_4px_0_rgba(0,0,0,0.04)] hover:tw-bg-[#F5F5F5] tw-transition-colors tw-h-12 tw-pl-2 tw-pr-5 tw-gap-3 tw-text-black focus-visible:tw-outline-focus tw-type-base'
      {...props}
    >
      <span className='tw-relative tw-size-8 tw-rounded-full tw-overflow-hidden tw-bg-black tw-flex-shrink-0'>
        <div className='tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-full'>
          <LazyVideoPlayer
            autoplay
            className='tw-h-full tw-w-full'
            clipEndTime={10}
            controls={false}
            fit='cover'
            loop
            muted
            ratio='1x1'
            rounded={false}
            src={videoUrl}
            withBorder={false}
            withPlayButton={false}
          />
        </div>
        <div className='tw-absolute tw-inset-0 tw-bg-black/40' />
        <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-10'>
          <Play16 className='tw-size-4 tw-text-white tw-opacity-90 group-hover:tw-opacity-100 tw-transition-opacity' />
        </div>
      </span>
      <span>{label}</span>
    </button>
  );
});
VideoCtaButton.displayName = "VideoCtaButton";

export const ANIMATION = {
  ALBUM_SCALE_DURATION: 1,
  LABEL_DELAY: 0.5,
  LABEL_DURATION: 1,
  CONTENT_DELAY: 0.7,
  CONTENT_DURATION: 1,
  get PLAY_BUTTON_DELAY() {
    return this.ALBUM_SCALE_DURATION;
  },
  PLAY_BUTTON_DURATION: 0.6,
} as const;

const WaveformIcon = () => (
  <m.svg
    xmlns='http://www.w3.org/2000/svg'
    width='12'
    height='12'
    viewBox='0 0 12 12'
    fill='none'
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.8 }}
    transition={{ duration: 0.15, ease: "easeInOut" }}
    aria-hidden='true'
  >
    <path
      d='M3.33329 0.666687V11.3334M0.666626 4.66669V7.33335M5.99996 3.33335V8.66669M8.66663 2.00002V10M11.3333 4.66669V7.33335'
      stroke='black'
      strokeWidth='1.33333'
      strokeLinecap='round'
    />
  </m.svg>
);

const PlayTriangleIcon = () => (
  <m.svg
    xmlns='http://www.w3.org/2000/svg'
    width='11'
    height='12'
    viewBox='0 0 11 12'
    fill='none'
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.8 }}
    transition={{ duration: 0.15, ease: "easeInOut" }}
    aria-hidden='true'
  >
    <path
      d='M9.75 4.5333C10.75 5.11065 10.75 6.55402 9.75 7.13137L2.25 11.4615C1.25 12.0389 1.31791e-06 11.3172 1.36838e-06 10.1625L1.74693e-06 1.50221C1.79741e-06 0.347507 1.25 -0.374179 2.25 0.203171L9.75 4.5333Z'
      fill='currentColor'
    />
  </m.svg>
);

interface TrackItemProps {
  sample: AudioSample;
  isPlaying: boolean;
  isActive: boolean;
  onClick: () => void;
  align?: "left" | "right";
}

function TrackItem({ sample, isPlaying, isActive, onClick, align = "left" }: TrackItemProps) {
  const showWaveform = isActive && isPlaying;

  return (
    <button
      className={clsx(
        "tw-group tw-w-full tw-transition-all tw-relative focus-visible:tw-outline-focus tw-pb-5 lg:tw-pb-0",
        align === "left" ? "tw-text-left" : "tw-text-left lg:tw-text-right",
        isActive ? "tw-opacity-100" : "tw-opacity-60 lg:tw-opacity-100",
      )}
      onClick={onClick}
      type='button'
    >
      <div
        className={clsx(
          "tw-absolute tw-top-0.5",
          align === "left" ? "tw-right-full lg:tw-mr-2" : "tw-left-full lg:tw-ml-2",
          showWaveform ? "tw-hidden lg:tw-block" : "tw-hidden",
        )}
      >
        <WaveformIcon />
      </div>

      <div
        className={clsx(
          "tw-absolute tw-top-0.5 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity",
          align === "left" ? "tw-right-full lg:tw-mr-2" : "tw-left-full lg:tw-ml-2",
          showWaveform ? "tw-hidden" : "tw-hidden lg:tw-block",
        )}
      >
        <PlayTriangleIcon />
      </div>

      <div className='tw-flex-1 tw-min-w-0 tw-space-y-2.5'>
        <p className='tw-text-black tw-type-base lg:tw-type-lg tw-overflow-x-clip tw-overflow-y-visible tw-text-ellipsis tw-whitespace-nowrap'>
          {sample.title || "Untitled"}
        </p>
        {sample.subtitle && (
          <p className='tw-text-gray-500 tw-type-xs tw-overflow-x-clip tw-overflow-y-visible tw-text-ellipsis tw-whitespace-nowrap'>
            {sample.subtitle}
          </p>
        )}
      </div>

      <DottedBorder className='lg:tw-hidden tw-pointer-events-none' side='bottom' />
    </button>
  );
}

type AlbumReleaseViewData = Pick<
  AlbumMusicPlayerBlock,
  "preTitle" | "title" | "shortDescription" | "primaryLink" | "videoCta" | "albumCover" | "albumDepthMap" | "items"
>;

type AlbumMusicReleaseRow = NonNullable<AlbumMusicPlayerBlock["releases"]>[number];

function primaryReleaseView(block: AlbumMusicPlayerBlock): AlbumReleaseViewData {
  const { releases: _releases, blockType: _bt, blockName: _bn, id: _id, tabLabel: _tabLabel, ...body } = block;
  return body;
}

function rowReleaseView(row: AlbumMusicReleaseRow): AlbumReleaseViewData {
  const { tabLabel: _tl, id: _rid, ...body } = row;
  return body;
}

type ReleaseEntry = {
  key: string;
  label: string;
  data: AlbumReleaseViewData;
};

function getProcessedSamplesFromRelease(data: AlbumReleaseViewData) {
  if (!Array.isArray(data.items)) return [];
  return data.items
    .filter(item => isEntity(item.audioSample))
    .map(item => ({
      sample: item.audioSample as AudioSample,
    }));
}

type PlaybackFocus = { instanceId: string; trackIndex: number };

const AlbumPlaybackContext = createContext<{
  reportPlayback: (instanceId: string, trackIndex: number) => void;
} | null>(null);

/** Same format as `togglePlay({ id })` for this release panel — single source of truth. */
function albumTrackId(instanceId: string, trackIndex: number) {
  return `album-${instanceId}-track-${trackIndex}`;
}

/**
 * Maps global `playingId` to a release row in this block (prefix scan per tab key — safe for hyphens in keys).
 */
function matchPlayingToEntries(entries: ReleaseEntry[], playingId: string | null) {
  if (!playingId) return null;
  for (const e of entries) {
    const prefix = `album-${e.key}-track-`;
    if (!playingId.startsWith(prefix)) continue;
    const suffix = playingId.slice(prefix.length);
    const trackIndex = Number.parseInt(suffix, 10);
    if (!Number.isFinite(trackIndex)) continue;
    const samples = getProcessedSamplesFromRelease(e.data);
    if (trackIndex < 0 || trackIndex >= samples.length) continue;
    return {
      instanceId: e.key,
      trackIndex,
      trackCount: samples.length,
      sample: samples[trackIndex].sample,
    };
  }
  return null;
}

/** Floating bar data: native audio when playing; otherwise last `reportPlayback` (e.g. paused). */
function resolveSharedFloatingModel(
  entries: ReleaseEntry[],
  playingId: string | null,
  isPaused: boolean,
  playbackFocus: PlaybackFocus | null,
) {
  const live = matchPlayingToEntries(entries, playingId);

  if (playingId && !live) return null;

  if (live && !isPaused) {
    return {
      instanceId: live.instanceId,
      trackIndex: live.trackIndex,
      trackCount: live.trackCount,
      sample: live.sample,
      isPlaying: true,
    };
  }

  if (!playbackFocus) return null;

  const entry = entries.find(e => e.key === playbackFocus.instanceId);
  if (!entry) return null;

  const samples = getProcessedSamplesFromRelease(entry.data);
  const sample = samples[playbackFocus.trackIndex]?.sample;
  if (!sample) return null;

  const expectedId = albumTrackId(playbackFocus.instanceId, playbackFocus.trackIndex);
  return {
    instanceId: playbackFocus.instanceId,
    trackIndex: playbackFocus.trackIndex,
    trackCount: samples.length,
    sample,
    isPlaying: playingId === expectedId && !isPaused,
  };
}

type AlbumSharedFloatingModel = NonNullable<ReturnType<typeof resolveSharedFloatingModel>>;

function nextTrackIndexCircular(index: number, trackCount: number) {
  return (index + 1) % trackCount;
}

function prevTrackIndexCircular(index: number, trackCount: number) {
  return index === 0 ? trackCount - 1 : index - 1;
}

function AlbumMultiReleaseFloatingChrome({
  model,
  playAt,
}: {
  model: AlbumSharedFloatingModel;
  playAt: (instanceId: string, index: number) => void;
}) {
  const { instanceId, trackIndex, trackCount, sample, isPlaying } = model;

  const handlePrevious = () => {
    if (trackCount === 0) return;
    playAt(instanceId, prevTrackIndexCircular(trackIndex, trackCount));
  };

  const handleNext = () => {
    if (trackCount === 0) return;
    playAt(instanceId, nextTrackIndexCircular(trackIndex, trackCount));
  };

  const handlePlayPause = () => {
    playAt(instanceId, trackIndex);
  };

  return (
    <FloatingMusicWidget
      currentTrack={sample}
      isPlaying={isPlaying}
      onNext={handleNext}
      onPlayPause={handlePlayPause}
      onPrevious={handlePrevious}
    />
  );
}

interface AlbumMusicPlayerReleasePanelProps {
  release: AlbumReleaseViewData;
  instanceId: string;
  /** Last release key that owned album playback (embedded floating bar when paused clears `playingId`). */
  lastPlaybackInstanceKey?: string | null;
  /**
   * When true, only the main block is hidden (inactive tab).
   */
  hideMainBlock?: boolean;
  /** When false, parent renders the floating bar (multi-release tabs). */
  embedFloatingChrome?: boolean;
  /** Register `playTrackAt` so the parent floating bar can drive playback (outside inert tab panels). */
  onRegisterPlayAt?: (instanceId: string, playAt: ((index: number) => void) | null) => void;
}

function AlbumMusicPlayerReleasePanel({
  release,
  instanceId,
  lastPlaybackInstanceKey = null,
  hideMainBlock = false,
  embedFloatingChrome = true,
  onRegisterPlayAt,
}: AlbumMusicPlayerReleasePanelProps) {
  const albumPlayback = useContext(AlbumPlaybackContext);
  const { togglePlay, playingId, isPaused, seek } = useNativeAudio();
  const [activeIndex, setActiveIndex] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  const currentTimeRef = useRef(0);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressButtonRef = useRef<HTMLButtonElement>(null);

  const primary = formatLink(release.primaryLink);
  const videoUrl = release.videoCta?.enabled ? getVideoUrl(release.videoCta.video) : undefined;
  const showVideoCta = release.videoCta?.enabled && videoUrl;

  const processedItems = useMemo(() => getProcessedSamplesFromRelease(release), [release]);
  const currentItem = processedItems[activeIndex];

  const playTrack = (index: number) => {
    const item = processedItems[index];
    if (!item) return;

    const audioUrl = getAudioUrl(item.sample.audio);
    if (!audioUrl) return;

    setActiveIndex(index);

    albumPlayback?.reportPlayback(instanceId, index);

    togglePlay({
      id: albumTrackId(instanceId, index),
      url: audioUrl,
      trackingProps: {
        track_title: item.sample.title,
        track_index: index,
      },
      onTimeUpdate: (time: number, dur: number) => {
        currentTimeRef.current = time;

        if (progressBarRef.current && dur > 0) {
          progressBarRef.current.style.width = `${(time / dur) * 100}%`;
        }

        if (progressButtonRef.current) {
          progressButtonRef.current.setAttribute("aria-valuenow", Math.round(time).toString());
        }
      },
      onDurationChange: (dur: number) => {
        setDuration(dur);
      },
      onPlayingChange: (isPlaying: boolean) => {
        if (isPlaying && !hasStartedPlaying) {
          setHasStartedPlaying(true);
        }
      },
      onEnded: () => {
        const nextIndex = (index + 1) % processedItems.length;
        playTrackRef.current(nextIndex);
      },
    });
  };

  const playTrackRef = useRef(playTrack);
  playTrackRef.current = playTrack;

  useEffect(() => {
    if (!onRegisterPlayAt) return;
    const playAt = (i: number) => playTrackRef.current(i);
    onRegisterPlayAt(instanceId, playAt);
    return () => {
      onRegisterPlayAt(instanceId, null);
    };
  }, [onRegisterPlayAt, instanceId, processedItems.length]);

  const handleSeek = useCallback(
    (time: number) => {
      seek(time);
      currentTimeRef.current = time;

      if (progressBarRef.current && duration > 0) {
        progressBarRef.current.style.width = `${(time / duration) * 100}%`;
      }

      if (progressButtonRef.current) {
        progressButtonRef.current.setAttribute("aria-valuenow", Math.round(time).toString());
      }
    },
    [seek, duration],
  );

  const handlePrevious = () => {
    const prevIndex = activeIndex === 0 ? processedItems.length - 1 : activeIndex - 1;
    playTrack(prevIndex);
  };

  const handleNext = () => {
    const nextIndex = (activeIndex + 1) % processedItems.length;
    playTrack(nextIndex);
  };

  const midPoint = Math.ceil(processedItems.length / 2);
  const leftColumnTracks = processedItems.slice(0, midPoint);
  const rightColumnTracks = processedItems.slice(midPoint);

  const isCurrentTrackPlaying = playingId === albumTrackId(instanceId, activeIndex) && !isPaused;

  const showEmbeddedFloating =
    embedFloatingChrome &&
    hasStartedPlaying &&
    (playingId?.startsWith(`album-${instanceId}-track-`) || (!playingId && lastPlaybackInstanceKey === instanceId));

  if (processedItems.length === 0) {
    return null;
  }

  return (
    <>
      {embedFloatingChrome && (
        <AnimatePresence>
          {showEmbeddedFloating && (
            <FloatingMusicWidget
              currentTrack={currentItem?.sample || null}
              isPlaying={isCurrentTrackPlaying}
              onPlayPause={() => playTrack(activeIndex)}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          )}
        </AnimatePresence>
      )}
      <div hidden={hideMainBlock} aria-hidden={hideMainBlock || undefined}>
        <Block className='block-el-overflow tw-container' name='album-music-player'>
          <div className='tw-flex tw-flex-col tw-gap-12 md:tw-gap-20'>
            {(release.preTitle || release.title || primary || showVideoCta || release.shortDescription) && (
              <div className='amp-animate-header tw-text-left md:tw-text-center tw-w-full tw-max-w-xl md:tw-mx-auto'>
                {release.preTitle && (
                  <p className='tw-type-lg tw-text-gray-500 tw-mb-5 lg:tw-mb-7'>{release.preTitle}</p>
                )}
                {release.title && <h1 className='tw-type-6xl tw-text-black tw-text-balance'>{release.title}</h1>}
                {release.shortDescription && (
                  <p className='tw-text-black tw-max-w-md md:tw-mx-auto tw-text-pretty tw-type-base tw-mt-6 lg:tw-hidden'>
                    {release.shortDescription}
                  </p>
                )}
                {(primary || showVideoCta) && (
                  <div className='tw-mt-10 tw-flex tw-flex-wrap tw-justify-start md:tw-justify-center tw-gap-2'>
                    {primary && (
                      <Link {...primary} asButton buttonVariants={{ size: "lg" }}>
                        {primary.label}
                      </Link>
                    )}
                    {showVideoCta && (
                      <ModalVideoPlayer
                        video={videoUrl}
                        customTrigger={
                          <VideoCtaButton videoUrl={videoUrl} label={release.videoCta?.label || "Watch Video"} />
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            <div className='tw-w-full tw-max-w-[800px] tw-mx-auto lg:tw-max-w-none'>
              <div className='tw-grid tw-grid-cols-2 lg:tw-grid-cols-[200px_1fr_200px] tw-gap-x-3 tw-gap-y-10 lg:tw-gap-6 lg:tw-items-center'>
                <div className='amp-animate-album tw-col-span-2 lg:tw-col-span-1 lg:tw-order-2 tw-flex tw-items-center tw-justify-center tw-h-full tw-z-10'>
                  <div className='tw-w-full lg:tw-max-w-[600px]'>
                    <AlbumArtPlayer
                      coverImage={release.albumCover}
                      depthMap={release.albumDepthMap}
                      currentTimeRef={currentTimeRef}
                      duration={duration}
                      progressBarRef={progressBarRef}
                      progressButtonRef={progressButtonRef}
                      isPlaying={isCurrentTrackPlaying}
                      onPlayPause={() => playTrack(activeIndex)}
                      onSeek={handleSeek}
                    />
                  </div>
                </div>

                <div className='amp-animate-left-tracks tw-flex tw-flex-col tw-gap-5 lg:tw-gap-6 tw-w-full lg:tw-max-w-[200px] lg:tw-order-1 lg:tw-justify-center'>
                  {leftColumnTracks.map((item, idx) => (
                    <TrackItem
                      isActive={activeIndex === idx}
                      isPlaying={isCurrentTrackPlaying}
                      key={`${instanceId}-left-${idx}-${item.sample.id}`}
                      onClick={() => playTrack(idx)}
                      sample={item.sample}
                    />
                  ))}
                </div>

                <div className='amp-animate-right-tracks tw-flex tw-flex-col tw-gap-5 lg:tw-gap-6 tw-w-full lg:tw-max-w-[200px] lg:tw-order-3 lg:tw-justify-center'>
                  {rightColumnTracks.map((item, idx) => {
                    const actualIndex = midPoint + idx;
                    return (
                      <TrackItem
                        isActive={activeIndex === actualIndex}
                        isPlaying={isCurrentTrackPlaying}
                        key={`${instanceId}-right-${actualIndex}-${item.sample.id}`}
                        onClick={() => playTrack(actualIndex)}
                        sample={item.sample}
                        align='right'
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {release.shortDescription && (
              <div className='amp-animate-description tw-text-center tw-w-full tw-hidden lg:tw-block'>
                <p className='tw-text-black tw-max-w-md tw-mx-auto tw-text-pretty tw-type-base'>
                  {release.shortDescription}
                </p>
              </div>
            )}
          </div>
        </Block>
      </div>
    </>
  );
}

export function BlockAlbumMusicPlayer(props: AlbumMusicPlayerBlock) {
  const t = useTranslations();
  const { playingId, isPaused } = useNativeAudio();
  const [playbackFocus, setPlaybackFocus] = useState<PlaybackFocus | null>(null);
  const playAtByPanelRef = useRef(new Map<string, (index: number) => void>());

  const reportPlayback = useCallback((instanceId: string, trackIndex: number) => {
    setPlaybackFocus({ instanceId, trackIndex });
  }, []);

  const registerPlayAt = useCallback((instanceId: string, playAt: ((index: number) => void) | null) => {
    const m = playAtByPanelRef.current;
    if (playAt) m.set(instanceId, playAt);
    else m.delete(instanceId);
  }, []);

  const invokePlayAt = useCallback((instanceId: string, index: number) => {
    playAtByPanelRef.current.get(instanceId)?.(index);
  }, []);

  const entries = useMemo((): ReleaseEntry[] => {
    const primaryData = primaryReleaseView(props);
    const primaryLabel =
      props.tabLabel?.trim() ||
      primaryData.title?.trim() ||
      primaryData.preTitle?.trim() ||
      t("album_music_player_primary_tab_fallback");
    const primary: ReleaseEntry = {
      key: "primary",
      label: primaryLabel,
      data: primaryData,
    };

    const extra = (props.releases ?? []).map((row, index) => {
      const data = rowReleaseView(row);
      const label =
        row.tabLabel?.trim() ||
        data.title?.trim() ||
        data.preTitle?.trim() ||
        t("album_music_player_extra_release_tab_fallback", { number: index + 2 });
      return {
        key: `release-${row.id || index}`,
        label,
        data,
      };
    });

    return [primary, ...extra];
  }, [props, t]);

  useEffect(() => {
    if (!playingId) return;
    if (!matchPlayingToEntries(entries, playingId)) {
      setPlaybackFocus(null);
    }
  }, [playingId, entries]);

  const multiRelease = entries.length > 1;
  const [selectedKey, setSelectedKey] = useState<Key>(() => entries[0]?.key ?? "primary");

  useEffect(() => {
    if (entries.some(e => String(e.key) === String(selectedKey))) return;
    setSelectedKey(entries[0]?.key ?? "primary");
  }, [entries, selectedKey]);

  const playbackApi = useMemo(() => ({ reportPlayback }), [reportPlayback]);

  const tabItems = multiRelease ? entries.map(e => ({ id: e.key, title: e.label })) : [];
  const tabsAriaLabel = props.title?.trim() || t("album_music_player_releases_tabs_aria_label");
  const sharedBar = multiRelease ? resolveSharedFloatingModel(entries, playingId, isPaused, playbackFocus) : null;

  return (
    <AlbumPlaybackContext.Provider value={playbackApi}>
      {!multiRelease ? (
        <AlbumMusicPlayerReleasePanel
          instanceId={entries[0]!.key}
          lastPlaybackInstanceKey={playbackFocus?.instanceId ?? null}
          release={entries[0]!.data}
        />
      ) : (
        <>
          <AnimatePresence>
            {sharedBar && <AlbumMultiReleaseFloatingChrome model={sharedBar} playAt={invokePlayAt} />}
          </AnimatePresence>
          <Tabs
            className='tw-flex tw-flex-col-reverse tw-gap-8 md:tw-gap-12'
            selectedKey={selectedKey}
            onSelectionChange={setSelectedKey}
          >
            <div className='tw-container'>
              <div className='tw-px-4 md:tw-px-12 amp-animate-tablist'>
                <TabList
                  aria-label={tabsAriaLabel}
                  centered
                  className='tw-mx-auto'
                  disableSelectionLayout
                  items={tabItems}
                  size='lg'
                />
              </div>
            </div>
            <TabPanels className='tw-min-w-0'>
              {entries.map(entry => (
                <TabPanel
                  key={entry.key}
                  shouldForceMount
                  className='tw-outline-none focus-visible:tw-outline-focus'
                  id={entry.key}
                >
                  {({ isInert }) => (
                    <AlbumMusicPlayerReleasePanel
                      embedFloatingChrome={false}
                      hideMainBlock={isInert}
                      instanceId={entry.key}
                      onRegisterPlayAt={registerPlayAt}
                      release={entry.data}
                    />
                  )}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </>
      )}
    </AlbumPlaybackContext.Provider>
  );
}
